<template>
  <div class="narrow-container mx-2 mt-4">
    <router-link
      class="app-title-left text-white mx-4 mt-4 mb-8"
      :to="{ name: 'site_forms_list' }"
    >
      <v-icon left class="text-white">mdi-arrow-left</v-icon>
      Back
    </router-link>
    <div v-if="!loading">
      <div class="SiteFormSettings__list mx-3 mt-8 pa-4" v-if="dataPresent">
        <h2 class="mb-2" style="display: inline-block">Data</h2>
        <span class="SiteFormSettings__header_actions">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                outlined
                small
                dark
                class="mr-2"
              >
                <v-icon left small>mdi-export-variant</v-icon>
                Export
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense dark>
              <v-list-item @click="downloadCsv">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-file-delimited-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>CSV</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            @click="removeMode = !removeMode"
            :outlined="!removeMode"
            :text="removeMode"
            small
            dark
          >
            <v-icon left small>{{ removeMode ? 'mdi-check' : 'mdi-pencil-outline' }}</v-icon>
            {{ removeMode ? 'Done' : 'Edit' }}
          </v-btn>
        </span>

        <v-divider />

        <v-data-table
          :headers="headers"
          v-model="selectedData"
          :show-select="removeMode"
          :items="form.data"
          item-key="form_data_id"
          class="organisations_access_members_table form_data_table"
          :sort_by="['timestamp']"
        >
          <template v-slot:top>
            <v-toolbar flat color="#232139">
              <v-toolbar-title>Recent Reponses</v-toolbar-title>
            </v-toolbar>
          </template>
          <template v-slot:item.status="{ item }" v-if="form.double_opt_in_enabled">
            <v-chip
              v-if="item.pending_confirmation"
              small
              color="amber darken-2"
              text-color="white"
            >
              Pending
            </v-chip>
            <v-chip
              v-else-if="item.confirmed_at"
              small
              color="green"
              text-color="white"
            >
              Confirmed
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <span class="remove" @click="removeObject(item)" :if="isOwner">
              <v-icon dark right color="red" class="ml-0" medium>
                mdi-delete-outline
              </v-icon>
            </span>
          </template>
          <template v-slot:footer>
            <v-btn
              @click="removeSelected"
              class="footer_remove_btn"
              color="red"
              :disabled="selectedData.length === 0"
              v-if="removeMode"
              dark
              small
            >
              <v-icon left small>mdi-delete-outline</v-icon>
              Delete{{ selectedData.length ? ` (${selectedData.length})` : '' }}
            </v-btn>
          </template>
        </v-data-table>
      </div>
      <div class="SiteFormSettings__list mx-3 mt-6">
        <h2 class="pa-4">Form Options</h2>
        <v-divider />
        <div class="SiteFormSettings__config pa-4">

          <v-switch
            color="y1"
            v-model="form.active"
            class="mb-3"
            @change="changeFormStatus"
            dense
            hide-details
            inset
            dark
          >
            <template v-slot:label>
              <span>{{
                form.active ? 'Form is active' : 'Form is inactive'
              }}</span>
            </template>
          </v-switch>

          <h3>Submit</h3>
          <p>How to handle form responses in your site</p>
          <div class="my-4">
            <v-checkbox
              v-model="form.ajax_form"
              @change="ajaxSubmitChanged($event)"
              label="AJAX Submit"
              class="mr-3"
              style="display: inline-block"
            ></v-checkbox>

            <v-checkbox
              v-model="form.ajax_without_message"
              @change="ajaxWithoutMessageSubmitChanged($event)"
              label="AJAX Submit without message"
              class="mr-3"
              style="display: inline-block"
            ></v-checkbox>

            <v-checkbox
              v-model="form.redirect_to_url"
              @change="redirectChanged($event)"
              label="Redirect on submit"
              style="display: inline-block"
            ></v-checkbox>

            <div v-if="form.ajax_form">
              <label class="SiteFormSettings__label"
                >Success message for user</label
              >
              <v-text-field
                v-model="form.ajax_message"
                class="SiteFormSettings__input"
                placeholder="Enter your custom message here"
                hide-details="auto"
                filled
                dark
                dense
              />
            </div>
            <div v-else-if="form.redirect_to_url">
              <label class="SiteFormSettings__label">URL for redirection</label>
              <v-text-field
                v-model="form.redirect_url"
                class="SiteFormSettings__input"
                placeholder="e.g https://somewebsite.com/thankyou.html or thankyou.html"
                hide-details="auto"
                filled
                dark
                dense
              />
            </div>
          </div>

          <h3 class="mt-8">Notifications</h3>
          <p>Choose who to notify when you receive responses</p>
          <v-checkbox
            v-model="form.notifications"
            label="Send me notifications for new responses"
          ></v-checkbox>

          <v-checkbox
            v-model="form.auto_response"
            label="Send auto-response to people who submitted the form with an email address"
          ></v-checkbox>

          <div v-if="form.auto_response" class="mt-2">
            <h4>Create your email</h4>
            <v-text-field
              v-model="form.email_address"
              class="SiteFormSettings__input mt-1"
              placeholder="from address"
              hide-details="auto"
              filled
              dark
              dense
            />

            <v-text-field
              v-model="form.email_subject"
              class="SiteFormSettings__input mt-1"
              placeholder="subject"
              hide-details="auto"
              filled
              dark
              dense
            />

            <v-textarea
              v-model="form.email_body"
              class="SiteFormSettings__input mt-1"
              placeholder="email body"
              hide-details="auto"
              filled
              dark
              dense
            />
          </div>

          <v-checkbox
            v-model="form.custom_email_enabled"
            label="Send notification to specified email address"
          ></v-checkbox>

          <div v-if="form.custom_email_enabled" class="mt-2">
            <h4>Type your custom email</h4>
            <v-text-field
              v-model="form.custom_email"
              class="SiteFormSettings__input mt-1"
              placeholder="custom email address"
              hide-details="auto"
              filled
              dark
              dense
            />
          </div>

          <v-checkbox
            v-model="form.collaborator_enabled"
            label="Send notification to collaborator"
          ></v-checkbox>

          <div v-if="form.collaborator_enabled" class="mt-2">
            <h4>Choose a collaborator or collaborators</h4>
            <v-select
              v-model="form.collaborator_emails"
              :items="form.collaborators"
              filled
              dense
              multiple
            />
          </div>

          <h3 class="mt-8">Email Confirmation</h3>
          <p>Require submitters to confirm their email before submissions are processed</p>
          <v-switch
            color="y1"
            v-model="form.double_opt_in_enabled"
            dense
            hide-details
            inset
            dark
            class="mb-3"
          >
            <template v-slot:label>
              <span>{{
                form.double_opt_in_enabled
                  ? 'Double opt-in enabled'
                  : 'Double opt-in disabled'
              }}</span>
            </template>
          </v-switch>
          <p v-if="form.double_opt_in_enabled" class="SiteFormSettings__hint mt-2">
            Webhooks and notifications will only fire after the submitter confirms via email.
          </p>
        </div>
        <div class="mx-4 mb-6">
          <v-btn @click="save" :disabled="saving" color="y1" class="mr-2" dark>
            Save
          </v-btn>

          <v-btn @click="cancel" :disabled="saving" color="n5" dark>
            Cancel
          </v-btn>
        </div>
      </div>

      <div class="SiteFormSettings__list mx-3 pa-4">
        <h2 class="py-4">Email Template</h2>
        <v-divider />
        <v-checkbox
          v-model="form.custom_template_enabled"
          :label="`Custom Email Template ${
            form.custom_template_enabled ? 'enabled' : 'disabled'
          }`"
        ></v-checkbox>
        <div v-if="form.custom_template_enabled">
          <label>You can customise your email template for this form. </label>
          <h3 class="mt-4 mb-2">From</h3>
          <label class="SiteFormSettings__label"
            >Set a custom 'from' email address
          </label>
          <v-text-field
            v-model="form.custom_from_text"
            class="SiteFormSettings__input"
            placeholder="custom email address"
            hide-details="auto"
            filled
            dark
            dense
          />

          <h3 class="mt-4 mb-2">Subject</h3>
          <label class="SiteFormSettings__label"
            >Set a custom 'subject' for the email
          </label>
          <v-text-field
            v-model="form.custom_subject_text"
            class="SiteFormSettings__input"
            placeholder="custom subject"
            hide-details="auto"
            filled
            dark
            dense
          />

          <h3 class="mt-8 mb-2">Add a header image</h3>
          <input
            type="file"
            style="background: #2f2e3f; color: black"
            @change="onFileChange"
          />

          <div>
            <img
              v-if="form.header_image_url"
              class="SiteFormSettings__preview"
              :src="form.header_image_url"
            />
          </div>
          <div class="SiteFormSettings__note_label pa-3 mt-2">
            <v-icon class="SiteFormSettings__note_icon">mdi-lightbulb</v-icon>
            Recommended image ratio is 5:1 for better display.
          </div>

          <h3 class="mt-8 mb-2">Personalise the email</h3>

          <v-textarea
            v-model="form.message_body"
            class="SiteFormSettings__input textarea mt-3"
            :placeholder="defaultBody"
            :auto-grow="true"
            rows="11"
            hide-details="auto"
            filled
            dark
            dense
          />

          <div class="SiteFormSettings__note_label pa-3 mt-2">
            <v-icon class="SiteFormSettings__note_icon">mdi-lightbulb</v-icon>

            Use these variables to personalise your emails.

            <div class="mt-4 pa-2">
              <div class="my-2">
                <span class="SiteFormSettings__note_pre">[user_name]</span> -
                This is the name of the user who receives a message.
              </div>
              <div class="my-2">
                <span class="SiteFormSettings__note_pre">[form_name]</span> -
                This is the name of the form that was sent.
              </div>
              <div class="my-2">
                <span class="SiteFormSettings__note_pre">[form_data]</span> -
                This is the data from completed forms.
              </div>
              <div class="my-2">
                <span class="SiteFormSettings__note_pre">[link_to_form]</span> -
                This is a link to your forms.
              </div>
            </div>
          </div>

          <div class="mb-4 mt-4">
            <v-btn
              @click="saveCustomConfig"
              :disabled="saving"
              color="y1"
              class="mr-2"
              dark
            >
              Save
            </v-btn>

            <v-btn @click="cancel" :disabled="saving" color="n5" dark>
              Cancel
            </v-btn>
          </div>
        </div>
      </div>

      <div class="SiteFormSettings__list mx-3 pa-4">
        <h2 class="py-4">Recaptcha Config</h2>
        <v-divider />
        <v-checkbox
          v-model="form.recaptcha_enabled"
          :label="`Recaptcha ${
            form.recaptcha_enabled ? 'enabled' : 'disabled'
          }`"
        ></v-checkbox>
        <div v-if="form.recaptcha_enabled">
          <label>Configure your reCAPTCHA keys for this form.</label>

          <div
            v-if="capthcaError.length > 0"
            class="SiteFormSettings__error-message mt-4"
          >
            {{ capthcaError }}
          </div>

          <div v-if="otherRecaptchaForms.length > 0" class="mt-4 mb-2">
            <label class="SiteFormSettings__label">Copy keys from another form</label>
            <v-select
              :items="otherRecaptchaForms"
              item-text="human_name"
              item-value="id"
              placeholder="Select a form..."
              @change="copyRecaptchaFrom"
              filled
              dark
              dense
              hide-details
              clearable
              class="SiteFormSettings__input"
            >
              <template v-slot:prepend-inner>
                <v-icon small class="mr-1">mdi-content-copy</v-icon>
              </template>
            </v-select>
          </div>

          <h3 class="mt-4 mb-2">Site Key</h3>
          <label class="SiteFormSettings__label">
            Set your reCAPTCHA site key
          </label>
          <v-text-field
            v-model="form.recaptcha_site_key"
            class="SiteFormSettings__input"
            placeholder="reCAPTCHA site key"
            :type="showSiteKey ? 'text' : 'password'"
            hide-details="auto"
            filled
            dark
            dense
          >
            <template #append>
              <v-btn
                @click="showSiteKey = !showSiteKey"
                icon
                small
                tabindex="-1"
              >
                <v-icon small>{{ showSiteKey ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
              <v-btn
                @click="copyToClipboard(form.recaptcha_site_key)"
                icon
                small
                tabindex="-1"
              >
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <h3 class="mt-4 mb-2">Secret Key</h3>
          <label class="SiteFormSettings__label">
            Set your reCAPTCHA secret key
          </label>
          <v-text-field
            v-model="form.recaptcha_server_key"
            class="SiteFormSettings__input"
            placeholder="reCAPTCHA secret key"
            :type="showSecretKey ? 'text' : 'password'"
            hide-details="auto"
            filled
            dark
            dense
          >
            <template #append>
              <v-btn
                @click="showSecretKey = !showSecretKey"
                icon
                small
                tabindex="-1"
              >
                <v-icon small>{{ showSecretKey ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
              <v-btn
                @click="copyToClipboard(form.recaptcha_server_key)"
                icon
                small
                tabindex="-1"
              >
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </div>
        <div class="mb-4 mt-4">
          <v-btn
            @click="saveCaptchaConfig"
            :disabled="saving"
            color="y1"
            class="mr-2"
            dark
          >
            Save
          </v-btn>

          <v-btn @click="cancel" :disabled="saving" color="n5" dark>
            Cancel
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { HEADER_IMAGE_BUCKET } from '@/env'
import { MODAL_ALERT } from '@/components/Modals'
import {
  canDeploySite,
  getProjectRole,
} from '@/utils/membershipPermissions'

export default {
  name: 'SiteFormSettings',
  components: {},
  data() {
    return {
      form: null,
      defaultForm: null,
      loading: true,
      saving: false,
      newImage: null,
      removeMode: false,
      selectedData: [],
      selectedCollaborators: [],
      headers: [],
      capthcaError: '',
      showSiteKey: false,
      showSecretKey: false,
    }
  },
  mounted() {
    this.$store.dispatch('form/setCurrent', this.$route.params.formId)
  },
  computed: {
    defaultBody() {
      let msg =
        'Hi, [user_name]\n' +
        'Your Forge Form [form_name]  has a new response:\n' +
        '\t[form_data]\n\n' +
        '\tLogin to view your responses [link_to_form].\n' +
        '\tYou can turn off or configure notifications for this form [link_to_form]\n\n' +
        'Happy Coding!\n' +
        'The Forge Robot'
      return msg
    },
    templateImage() {
      return this.form.header_image_url
    },
    dataPresent() {
      return this.form.form_datum_ids.length > 0
    },
    otherRecaptchaForms() {
      if (!this.form) return []
      return this.$store.state.form.list.filter(
        (f) => f.id !== this.form.id && f.recaptcha_site_key && f.recaptcha_server_key
      )
    },
  },
  methods: {
    ajaxSubmitChanged(e) {
      if (e) {
        this.form.ajax_without_message = false
        this.form.redirect_to_url = false
      }
    },
    copyToClipboard(value) {
      if (!value) return
      navigator.clipboard.writeText(value).then(() => {
        this.$root.$emit('showSnack', { message: 'Copied to clipboard', color: 'success' })
      })
    },
    copyRecaptchaFrom(formId) {
      if (!formId) return
      const source = this.$store.state.form.list.find((f) => f.id === formId)
      if (!source) return
      this.form.recaptcha_site_key = source.recaptcha_site_key
      this.form.recaptcha_server_key = source.recaptcha_server_key
    },
    removeObject(item) {
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Remove Form Data',
          text: 'Do you really want to remove this data?',
          actionBtnLabel: 'Remove',
          showCancelBtn: true,
          withSpiner: false,
          action: () => {
            this.$store.dispatch('form/deleteFormData', {
              form_id: this.form.id,
              id: item.form_data_id,
            })
          },
        },
      })
    },
    removeSelected() {
      let dataIds = this.selectedData.map((sd) => sd.form_data_id)
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Remove Form Data',
          text: 'Do you really want to remove selected data?',
          actionBtnLabel: 'Remove',
          showCancelBtn: true,
          withSpiner: false,
          action: () => {
            this.$store
              .dispatch('form/bulkDeleteFormData', {
                form_id: this.form.id,
                data_ids: dataIds,
              })
              .then(() => {
                this.selectedData = []
              })
          },
        },
      })
    },
    isOwner() {
      const org = this.$store.state.organisations.current
      const activeSite = this.$store.state.sites.active
      const project = activeSite?.projectId
        ? this.$store.getters['projects/findById'](activeSite.projectId)
        : this.$store.state.projects.active

      return canDeploySite(org, getProjectRole(project))
    },
    onFileChange(e) {
      const file = e.target.files[0]
      this.newImage = e.target.files[0]
      this.form.header_image_url = URL.createObjectURL(file)
    },
    ajaxWithoutMessageSubmitChanged(e) {
      if (e) {
        this.form.ajax_form = false
        this.form.redirect_to_url = false
      }
    },
    redirectChanged(e) {
      if (e) {
        this.form.ajax_without_message = false
        this.form.ajax_form = false
      }
    },
    changeFormStatus() {
      if (this.saving) return
      this.saving = true
      const params = {
        form_id: this.form.id,
        form: {
          active: this.form.active
        }
      }
      this.$store
        .dispatch('form/update', params)
        .then((res) => {
          this.saving = false
        })
        .catch((e) => {
          console.log(e)
          this.saving = false
        })
    },
    save() {
      if (this.saving) return
      this.saving = true
      this.updateForm()
    },
    updateForm(customEmail = false) {
      let params = customEmail
        ? this.form.getCustomEmailObject()
        : this.form.getServerObject()
      params = {
        form_id: this.form.id,
        form: params,
      }
      this.$store
        .dispatch('form/update', params)
        .then((res) => {
          this.saving = false
        })
        .catch((e) => {
          console.log(e)
          this.saving = false
        })
    },
    cancel() {
      this.form = _.cloneDeep(this.defaultForm)
    },
    downloadCsv() {
      this.$store.dispatch('form/downloadCsv', this.form.id).then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Form_Data.csv')
        document.body.appendChild(link)
        link.click()
      })
    },
    saveCaptchaConfig() {
      if (this.saving) return
      this.saving = true
      this.capthcaError = ''
      if (
        this.form.recaptcha_enabled &&
        (this.form.recaptcha_site_key == null ||
          this.form.recaptcha_site_key.length < 4 ||
          this.form.recaptcha_server_key == null ||
          this.form.recaptcha_server_key.length < 4)
      ) {
        this.capthcaError = 'Site Key and Server Key should be added'
        this.saving = false
        return
      }
      let params = this.form.getCaptchaObject()
      params = {
        form_id: this.form.id,
        form: params,
      }
      this.$store
        .dispatch('form/update', params)
        .then(() => {
          this.saving = false
        })
        .catch((e) => {
          console.log(e)
          this.saving = false
        })
    },
    saveCustomConfig() {
      if (this.saving) return
      this.saving = true
      if (this.newImage != null) {
        let policyParams = {
          form_id: this.form.id,
          file_name: this.newImage.name,
        }
        this.$store
          .dispatch('form/getPolicy', policyParams)
          .then((res) => {
            const formData = new FormData()
            for (let param in res.body) formData.append(param, res.body[param])
            formData.append('file', this.newImage)
            this.uploadPromise = this.$http
              .post(HEADER_IMAGE_BUCKET, formData, {
                before(request) {
                  this.previousRequest = request
                },
              })
              .then(() => {
                this.form.header_image_url =
                  HEADER_IMAGE_BUCKET + '/' + res.body.key
                this.newImage = null
                this.updateForm(true)
              })
              .catch((e) => {
                console.log(e)
                this.saving = false
              })
          })
          .catch((e) => {
            console.log(e)
          })
      } else {
        this.updateForm(true)
      }
    },
  },
  watch: {
    '$store.state.form.currentForm': {
      handler(newVal, oldVal) {
        this.form = _.cloneDeep(newVal)
        this.defaultForm = _.cloneDeep(newVal)
        this.loading = false
        this.headers = []
        this.form.fields.forEach((element) => {
          let header = {
            text: element,
            value: element.replace(/ /g, '_').toLowerCase(),
          }
          this.headers.push(header)
        })
        if (this.form.double_opt_in_enabled) {
          this.headers.push({ text: 'Status', value: 'status', sortable: true })
        }
        this.headers.push({ text: '', value: 'actions', sortable: false })
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss">
$style: SiteFormSettings;

.form_data_table {
  background-color: #232139 !important;
  tbody tr:hover {
    background: #3c3a55 !important;
  }
  tbody tr.v-data-table__selected {
    background-color: #3c3a55 !important;
  }
  .footer_remove_btn {
    position: absolute;
    margin-top: 15px;
    margin-left: 15px;
  }
}

.#{$style} {
  &__backBtn {
    color: #cacaca !important;
    font-size: 14px;
    text-decoration: none;
    cursor: pointer;
    &:hover {
      text-decoration: none;
      color: white;
    }
  }
  &__header_actions {
    float: right;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -5px;

    .v-btn--outlined {
      border-color: rgba(255, 255, 255, 0.25) !important;
      color: rgba(255, 255, 255, 0.6) !important;
      &:hover {
        border-color: rgba(255, 255, 255, 0.45) !important;
        color: rgba(255, 255, 255, 0.85) !important;
      }
    }
    .v-btn--text {
      color: rgba(255, 255, 255, 0.6) !important;
      &:hover {
        color: rgba(255, 255, 255, 0.85) !important;
      }
    }
  }
  &__list {
    background: #1f1e31;
    border: 1px solid #2e2c42;
    border-radius: 8p;
  }
  &__label {
    @extend %label-styles;
  }
  &__hint {
    color: $N2;
    font-size: 13px;
  }
  &__error-message {
    background: rgba($R1, 0.3);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 26px;
    &._last {
      color: $N2;
    }
  }
  &__note_label {
    background: #fcf4d4;
    border: 1px solid #f7e9ab;
    color: #9b7406;
    border-radius: 3px;
  }
  &__note_icon {
    color: #9b7406 !important;
    font-size: 16px;
    margin-right: 4px;
  }
  &__note_pre {
    padding: 2px 4px;
    font-weight: bold;
    background: #c89509;
    color: #6a4f04;
    border-radius: 3px;
    border: 1px solid #6a4f04;
  }
  &__input {
    @extend %input-styles;
    margin-bottom: 16px;
    &.textarea {
      width: 502px;
      overflow-y: auto;
    }
  }
  &__preview {
    width: 502px;
    height: 100px;
  }
}

.app-title-left {
  position: relative;
  left: 0;
  top: 0;
  height: 100%;
  cursor: pointer;
  opacity: 0.8;
  text-decoration: none;
  &:hover {
    opacity: 1;
  }
}

.text-white {
  color: #cacaca !important;
}
</style>
